/**
 * The game simulation's state in one object (the concept's `G` plus the jet, concept L892–L895), shared by the rule
 * and entity modules. Types only: src/sim/gameSim.ts builds and steps it.
 */
import type { Rng } from '../core/rng.ts';
import type { EnemyPool } from '../entities/pool.ts';
import type { WorldProfile } from '../world/profiles.ts';
import type { FlightState } from './flight.ts';
import type { Fx } from './fx.ts';
import type { BridgeState, CrashCause, Enemy, List, Rules, Shot, SimEvent, SimEventType, Tracer } from './state.ts';

/**
 * Randomness by stream (GDD §5.3, master plan porting rule 3; judgment call 20). Production: the section stream is
 * reseeded from (run seed, section index) whenever a section is built, so a checkpoint rebuilds it identically; the
 * combat stream is seeded per run; the effects stream is the game's own. The parity harness replays the concept's
 * draws through the same three streams instead.
 */
export interface Streams {
  section: Rng;
  combat: Rng;
  fx: Rng;
  beginSection(runSeed: number, index: number): void;
  beginRun(runSeed: number): void;
}

export interface RunState {
  /** Player run (D20: only these set the high score) or attract-mode demo. */
  player: boolean;
  /**
   * The title screen's demo (M5, judgment call 28): bridges don't raise its level, so the player can take the run
   * over without a cut. Off in tests, whose autopilot runs climb the levels.
   */
  demo: boolean;
  runSeed: number;
  level: number;
  /** Jets left, the one flying included (GDD §4.6). */
  lives: number;
  score: number;
  hi: number;
  fuel: number;
  alive: boolean;
  /** Seconds the wreck still burns. */
  deadT: number;
  invuln: number;
  /** GAME OVER seconds left; `overShown` once it has started. */
  over: number;
  overShown: boolean;
  /** Title fade seconds left (GDD §7.1): no enemy fire while > 0, no collisions while > 3.5 (concept L1118). */
  title: number;
  /** The autopilot's dodge skill (concept L1073). */
  skill: number;
  /** The autopilot shoots the bridge from this range (drawn per section, concept L945). */
  bridgeRange: number;
  /** Concept rules only: back off 170 m after a bridge crash (D9). */
  backoff: boolean;
  /** The jet is drawn (it blinks while invulnerable). */
  jetVisible: boolean;
  /** Level the first run starts at (concept `startLevel`); later runs start at 1. */
  firstLevel: number;
  /** Next score that earns a jet (D11). */
  nextExtra: number;
  /** Checkpoint z: 150 m past the last destroyed bridge, or the run start (Q2). */
  checkpointZ: number;
  /** Where the HUD's progress bar starts: the last destroyed bridge, or the run start (D19). */
  progressFrom: number;
  /** The section a checkpoint restart rebuilds: its index and spawn range (world z). */
  sectionIndex: number;
  sectionZA: number;
  sectionZB: number;
  /** Runs started (attract and player). */
  runs: number;
  /** The last jet lost, for tests and the HUD. */
  lastCause: CrashCause | null;
  crashes: number;
}

export interface GunState {
  /** Seconds until the cannons can fire again. */
  fireT: number;
  /** Volleys fired. */
  burst: number;
  /** Next tracer in the ring. */
  next: number;
}

export interface GameSim {
  readonly W: WorldProfile;
  readonly rules: Rules;
  /** Terrain height at (x, z), as drawn (bank collisions, GDD §4.4). */
  readonly ground: (x: number, z: number) => number;
  readonly fx: Fx;
  readonly streams: Streams;
  /** The player's jet; respawns move it. */
  readonly jet: FlightState;
  /** New run seed (production: crypto; tests: deterministic). */
  readonly nextRunSeed: () => number;
  readonly run: RunState;
  readonly guns: GunState;
  readonly pool: EnemyPool;
  /** Live enemies in spawn order (the concept's G.ents). */
  readonly ents: List<Enemy>;
  /** Every enemy shot slot (48) and the live ones in firing order (G.shots). */
  readonly shotSlots: Shot[];
  readonly shotUsed: Uint8Array;
  readonly shots: List<Shot>;
  /** Player tracers: 24 (D14), or 18 under concept rules. */
  readonly tracers: Tracer[];
  /** Enemy-jet triggers: when the jet passes z, a jet enters (concept G.jetTrig). */
  readonly jetTrig: Float64Array;
  jetTrigCount: number;
  readonly bridge: BridgeState;
  /** Spacing slots used while a section spawns (scratch). */
  readonly slots: Float64Array;
  /** Event ring for the HUD (M5) and tests; `eventCount` counts every event ever pushed. */
  readonly events: SimEvent[];
  eventCount: number;
  /** Seconds simulated (drives bobbing, beacons and rotors). */
  t: number;
}

export function pushEvent(sim: GameSim, type: SimEventType, level = 0, cause: CrashCause | null = null): void {
  const e = sim.events[sim.eventCount % sim.events.length];
  if (e) {
    e.type = type;
    e.level = level;
    e.cause = cause;
  }
  sim.eventCount++;
}
